
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Card, CardBody, CardTitle, Form, FormGroup, Input, Label, Row, Col, Button } from "reactstrap";
import Copyright from "components/Common/copyright";
import { CreditCardService } from "services/CreditCardService";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import logo from "../../assets/images/logo.png";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "context/AuthProvider";
import { environment } from "environment/environment";
import { PaymentType } from "enum/PaymentOption";
import visaLogo from '../../assets/images/paymentcards/Visa.png';
import mastercardLogo from '../../assets/images/paymentcards/MasterCard.png';
import emptyLogo from '../../assets/images/paymentcards/empty.jpg';
import { CreditCards } from "models/CreditCard";

const stripePromise = loadStripe(environment.STRIPE_PUBLIC_KEY);

const PaymentCNewardsInner: React.FC = () => {
  const [user] = useAuth();
  const { promoCode } = useParams<{ promoCode?: string }>();
  const [attachedCards, setAttachedCards] = useState<CreditCards[]>([]);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [cardholderEmail, setCardholderEmail] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [formErrors, setFormErrors] = useState({ email: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [attachedCardsCount, setAttachedCardsCount] = useState(0);
  const [saveCard, setSaveCard] = useState(false);
  const { orderIdList, paymentType, totalFare } = location.state || {};

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    fetchAttachedCards();
  }, []);

  const fetchAttachedCards = async () => {
    try {
      const paymentCards = await CreditCardService.getAttachedPaymentMethods();
      if (paymentCards.data.length > 0) {
        setAttachedCards(paymentCards.data);
        setAttachedCardsCount(paymentCards.data.length);
      } else {
        setAttachedCards([]);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
  };

  const handleCardSelect = async (cardId: string) => {
    setSelectedCard(cardId);

    if (!cardId) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'No payment method selected!',
      });
      return;
    }

    const confirmationResult = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to proceed with this payment?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, proceed!',
      cancelButtonText: 'No, cancel!',
      confirmButtonColor: "#F75D21",
      cancelButtonColor: "#A9A9A9",
    });

    if (confirmationResult.isDismissed || confirmationResult.isDenied) {
      setSelectedCard(null);
      return;
    }

    try {
      const customerResponse = await CreditCardService.PayByCard({
        paymentType: paymentType,
        orderIdList: orderIdList,
        paymentMethodId: cardId,
        promoCode: promoCode,
      });

      if (customerResponse.success) {
        const successRoute = paymentType === PaymentType.INVOICE
          ? `/${user?.role}/payment-success/invoice/${customerResponse.data.orderId}`
          : `/${user?.role}/payment-success/${customerResponse.data.orderId}`;
        navigate(successRoute);
      } else {
        navigate(`/${user?.role}/payment-error`);
      }
    } catch (error) {
      console.error('Error during payment:', error);
      navigate(`/${user?.role}/payment-error`);
    }
  };

  const validateEmail = (email: string) => {
    if (!email) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Invalid email format";
    return "";
  };

  const validateName = (name: string) => {
    if (!name) return "Cardholder name is required";
    return "";
  };

  const handlePay = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const emailError = validateEmail(cardholderEmail);
    const nameError = validateName(cardholderName);
    if (emailError || nameError) {
      setFormErrors({ email: emailError, name: nameError });
      return;
    }

    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          email: cardholderEmail,
          name: cardholderName,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      const shouldSaveCard = attachedCardsCount < 3 ? saveCard : false;
      const response = await CreditCardService.PayNewCard({
        paymentType,
        orderIdList,
        paymentMethodId: paymentMethod.id,
        saveCard: shouldSaveCard,
        promoCode: promoCode,
      });

      if (response.success) {
        const successRoute = paymentType === PaymentType.INVOICE
          ? `/${user?.role}/payment-success/invoice/${response.data.orderId}`
          : `/${user?.role}/payment-success/${response.data.orderId}`;

        navigate(successRoute);
      } else {
        navigate(`/${user?.role}/payment-error`);
      }
    } catch (error) {
      console.error("Error during payment:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container p-4 shadow-md">
      <section className="my-4">
        <div className="position-relative">
          <Row className="justify-content-center">
            <Col lg="5" md="12" sm="12" className="d-flex align-items-center justify-content-center">
              <Card className="w-100">
                <CardBody>
                  <div>
                    <Button
                      color="outline-none"
                      className="mb-3 d-flex align-items-center "
                      onClick={() => navigate(`/${user?.role}/payment-error`)}
                    >
                      <span className="me-2">&#8592;</span> {/* Back arrow symbol */}

                    </Button>
                  </div>
                  <div className="sign-logo mb-3">

                    <img src={logo} alt="" />

                  </div>
                  <h4 className="title mb-1">Payment Gateway</h4>

                  <Form autoComplete="off" onSubmit={(event) => {
                    event.preventDefault();
                    void handlePay(event);

                  }}>
                    {error && <div className="text-danger mb-3">{error}</div>}
                    <FormGroup>
                      <Label for="cardholderEmail">Email:</Label>
                      <Input
                        autoComplete="off"
                        type="email"
                        id="cardholderEmail"
                        value={cardholderEmail}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCardholderEmail(value);
                          setFormErrors((prev) => ({ ...prev, email: validateEmail(value) }));
                        }}
                        invalid={!!formErrors.email}
                      />
                      {formErrors.email && <div className="text-danger small">{formErrors.email}</div>}
                    </FormGroup>
                    <FormGroup>
                      <Label for="cardholderName">Cardholder Name:</Label>
                      <Input
                        autoComplete="off"
                        type="text"
                        id="cardholderName"
                        value={cardholderName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCardholderName(value);
                          setFormErrors((prev) => ({ ...prev, name: validateName(value) }));
                        }}
                        invalid={!!formErrors.name}
                      />
                      {formErrors.name && <div className="text-danger small">{formErrors.name}</div>}
                    </FormGroup>
                    <FormGroup>
                      <Label>Card Details:</Label>
                      <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '10px' }}>
                        <CardElement
                          options={{
                            hidePostalCode: true,
                            style: {
                              base: {
                                fontSize: '18px',
                                color: '#32325d',
                                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                                '::placeholder': {
                                  color: '#aab7c4',
                                },
                                padding: '10px',
                                backgroundColor: 'white',
                              },
                              invalid: {
                                color: '#fa755a',
                                iconColor: '#fa755a',
                              },
                            },
                          }}
                        />
                      </div>

                    </FormGroup>
                    <FormGroup check>
                      <Input
                        type="checkbox"
                        id="saveCard"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        disabled={attachedCardsCount >= 3}
                      />
                      <Label for="saveCard" check>
                        Remember this card
                      </Label>

                    </FormGroup>
                    {attachedCardsCount >= 3 && (
                      <div className="text-muted small mt-2 text-left">
                        You can only save up to 3 cards. This card will not be saved.
                      </div>
                    )}

                    <div className="d-flex justify-content-center flex-column align-items-center">

                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading}
                        className="mt-3"
                      >
                        {loading ? "Processing..." : `Pay Now $ ${(Number(totalFare) || 0).toFixed(2)}`}
                      </Button>
                    </div>
                  </Form>

                  <div className="mt-4">
                    <div className="d-flex align-items-center my-4">
                      <div className="flex-grow-1 border-top"></div>
                      <span className="mx-3 text-muted">Or use saved cards</span>
                      <div className="flex-grow-1 border-top"></div>
                    </div>

                    {attachedCards.length > 0 ? (
                      attachedCards.map((card) => (
                        <FormGroup key={card.id} className="mb-3">
                          <Label
                            className="d-flex align-items-center justify-content-between p-3 bg-light shadow-sm rounded"
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex align-items-center gap-4">
                              <img
                                src={
                                  card.brand === "visa"
                                    ? visaLogo
                                    : card.brand === "mastercard"
                                      ? mastercardLogo
                                      : emptyLogo
                                }
                                alt={card.brand}
                                style={{ width: "40px" }}
                              />
                              <Col>
                                <Row>{card.brand.toUpperCase()}</Row>
                                <Row>**** **** **** {card.last4}</Row>
                              </Col>
                            </div>
                            <Input
                              type="radio"
                              name="paymentCard"
                              value={card.id}
                              checked={selectedCard === card.id}
                              onChange={() => {
                                handleCardSelect(card.id).catch((error) => {
                                  console.error("Error in handleRememberCard:", error);
                                });
                              }}
                              className="me-3"
                            />
                          </Label>
                        </FormGroup>
                      ))
                    ) : (
                      <div className="text-center text-muted small mt-2">
                        <p>No saved cards found. You can enter your card details above and select 'Remember Card' to save it for future use</p>
                      </div>
                    )}
                  </div>

                  <div className="w-100 text-center mt-5">
                    <Copyright />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </section>
    </div>
  );
};

const CardPayment: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentCNewardsInner />
    </Elements>
  );
};

export default CardPayment;